// frontend/src/components/Logs.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    AccessTime,
    ContentCopy,
    Description,
    ErrorOutlineOutlined as ErrorOutline,
    FolderOpen,
    Refresh,
    WarningAmber,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';
import Layout from './Layout';
import { useSnackbar } from '../contexts/SnackbarContext';

const DEFAULT_LINE_LIMIT = 200;
const AUTO_REFRESH_OPTIONS = [15, 30, 60];
const LINE_LIMIT_OPTIONS = [100, 200, 500, 1000];
const LEVEL_CHIP_COLOR = {
    error: 'error',
    warning: 'warning',
    info: 'info',
    debug: 'default',
};

const formatBytes = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(Math.floor(Math.log(numericValue) / Math.log(1024)), units.length - 1);
    const scaledValue = numericValue / (1024 ** exponent);

    return `${scaledValue.toFixed(scaledValue >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const formatDateTime = (value) => {
    if (!value) {
        return 'Unknown';
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm:ss') : String(value);
};

const getLogKey = (log) => `${log.sourceId}::${log.filename}`;
const getEntryDisplayMessage = (entry) => entry?.messageText || entry?.message || '';

const SummaryCard = ({ title, value, helperText, icon, tone = 'default' }) => {
    const iconColor = {
        primary: 'primary.main',
        warning: 'warning.main',
        error: 'error.main',
        default: 'text.primary',
    }[tone] || 'text.primary';

    const iconBackground = {
        primary: 'rgba(25, 118, 210, 0.08)',
        warning: 'rgba(237, 108, 2, 0.08)',
        error: 'rgba(211, 47, 47, 0.08)',
        default: 'grey.100',
    }[tone] || 'grey.100';

    return (
        <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        {helperText}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: iconBackground,
                        color: iconColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
            </CardContent>
        </Card>
    );
};

const Logs = () => {
    const { showSnackbar } = useSnackbar();
    const viewerRef = useRef(null);

    const [sources, setSources] = useState([]);
    const [availableLogs, setAvailableLogs] = useState([]);
    const [selectedLogKey, setSelectedLogKey] = useState('');
    const [logEntries, setLogEntries] = useState([]);
    const [logMeta, setLogMeta] = useState(null);
    const [listLoading, setListLoading] = useState(false);
    const [entriesLoading, setEntriesLoading] = useState(false);
    const [fileSearchTerm, setFileSearchTerm] = useState('');
    const [lineSearchInput, setLineSearchInput] = useState('');
    const [appliedLineSearch, setAppliedLineSearch] = useState('');
    const [severityFilter, setSeverityFilter] = useState('all');
    const [direction, setDirection] = useState('desc');
    const [lineLimit, setLineLimit] = useState(DEFAULT_LINE_LIMIT);
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
    const [autoRefreshSeconds, setAutoRefreshSeconds] = useState(AUTO_REFRESH_OPTIONS[0]);
    const [selectedEntryId, setSelectedEntryId] = useState('');

    useEffect(() => {
        const debounceTimer = window.setTimeout(() => {
            setAppliedLineSearch(lineSearchInput.trim());
        }, 300);

        return () => window.clearTimeout(debounceTimer);
    }, [lineSearchInput]);

    const selectedLog = useMemo(
        () => availableLogs.find((log) => getLogKey(log) === selectedLogKey) || null,
        [availableLogs, selectedLogKey]
    );

    const selectedEntry = useMemo(
        () => logEntries.find((entry) => entry.id === selectedEntryId) || null,
        [logEntries, selectedEntryId]
    );

    const summary = useMemo(() => {
        const pm2Files = availableLogs.filter((log) => log.sourceId === 'PM2').length;
        const errorFiles = availableLogs.filter((log) => log.type === 'error').length;
        const latestLog = [...availableLogs]
            .sort((a, b) => new Date(b.modifiedAt) - new Date(a.modifiedAt))[0];

        return {
            totalFiles: availableLogs.length,
            pm2Files,
            errorFiles,
            latestLog,
        };
    }, [availableLogs]);

    const groupedLogs = useMemo(() => {
        const normalizedSearch = fileSearchTerm.trim().toLowerCase();

        return sources.map((source) => ({
            ...source,
            logs: availableLogs.filter((log) => (
                log.sourceId === source.id
                && (
                    !normalizedSearch
                    || `${log.filename} ${log.sourceLabel} ${log.type}`.toLowerCase().includes(normalizedSearch)
                )
            )),
        }));
    }, [availableLogs, fileSearchTerm, sources]);

    const fetchAvailableLogs = useCallback(async () => {
        setListLoading(true);

        try {
            const response = await axios.get('/api/logs');
            const nextSources = Array.isArray(response.data.sources) ? response.data.sources : [];
            const nextLogs = Array.isArray(response.data.logs) ? response.data.logs : [];

            setSources(nextSources);
            setAvailableLogs(nextLogs);
            setSelectedLogKey((currentKey) => {
                if (currentKey && nextLogs.some((log) => getLogKey(log) === currentKey)) {
                    return currentKey;
                }

                const pm2Log = nextLogs.find((log) => log.sourceId === 'PM2' && log.readable);
                const readableLog = nextLogs.find((log) => log.readable);
                const fallbackLog = pm2Log || readableLog || null;

                return fallbackLog ? getLogKey(fallbackLog) : '';
            });
        } catch (error) {
            console.error('Error fetching available logs:', error);
            showSnackbar('Error fetching available logs.', 'error');
        } finally {
            setListLoading(false);
        }
    }, [showSnackbar]);

    const fetchLogEntries = useCallback(async (log) => {
        if (!log) {
            setLogEntries([]);
            setLogMeta(null);
            setSelectedEntryId('');
            return;
        }

        setEntriesLoading(true);

        try {
            const response = await axios.get(
                `/api/logs/${encodeURIComponent(log.sourceId)}/${encodeURIComponent(log.filename)}`,
                {
                    params: {
                        limit: lineLimit,
                        direction,
                        level: severityFilter,
                        search: appliedLineSearch,
                    },
                }
            );

            const nextEntries = Array.isArray(response.data.entries)
                ? response.data.entries
                : (Array.isArray(response.data.logs) ? response.data.logs : []);

            setLogEntries(nextEntries);
            setLogMeta(response.data.meta || null);
            setSelectedEntryId((currentId) => (
                nextEntries.some((entry) => entry.id === currentId)
                    ? currentId
                    : (nextEntries[0]?.id || '')
            ));
        } catch (error) {
            console.error('Error fetching log entries:', error);
            showSnackbar('Error fetching log entries.', 'error');
        } finally {
            setEntriesLoading(false);
        }
    }, [appliedLineSearch, direction, lineLimit, severityFilter, showSnackbar]);

    const handleRefresh = useCallback(async () => {
        await fetchAvailableLogs();

        if (selectedLog) {
            await fetchLogEntries(selectedLog);
        }
    }, [fetchAvailableLogs, fetchLogEntries, selectedLog]);

    useEffect(() => {
        fetchAvailableLogs();
    }, [fetchAvailableLogs]);

    useEffect(() => {
        fetchLogEntries(selectedLog);
    }, [fetchLogEntries, selectedLog]);

    useEffect(() => {
        if (!autoRefreshEnabled) {
            return undefined;
        }

        const refreshTimer = window.setInterval(() => {
            handleRefresh();
        }, autoRefreshSeconds * 1000);

        return () => window.clearInterval(refreshTimer);
    }, [autoRefreshEnabled, autoRefreshSeconds, handleRefresh]);

    const handleCopySelected = async () => {
        if (!selectedEntry) {
            showSnackbar('Select a log entry to copy.', 'info');
            return;
        }

        try {
            const contentToCopy = [
                selectedEntry.timestamp ? `${selectedEntry.timestamp}:` : null,
                selectedEntry.message,
            ]
                .filter(Boolean)
                .join('\n');

            await navigator.clipboard.writeText(contentToCopy);
            showSnackbar('Selected log entry copied to clipboard.');
        } catch (error) {
            console.error('Error copying log entry:', error);
            showSnackbar('Unable to copy the selected log entry.', 'error');
        }
    };

    const handleScrollToLatest = () => {
        if (!viewerRef.current) {
            return;
        }

        viewerRef.current.scrollTo({
            top: direction === 'desc' ? 0 : viewerRef.current.scrollHeight,
            behavior: 'smooth',
        });
    };

    return (
        <Layout>
            <Box sx={{ p: 3 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            Application Logs
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Review PM2, DPSAPI, and Shopify application logs in one operational view.
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant="outlined" startIcon={<AccessTime />} onClick={handleScrollToLatest} disabled={!selectedLog}>
                            Scroll to latest
                        </Button>
                        <Button variant="outlined" startIcon={<ContentCopy />} onClick={handleCopySelected} disabled={!selectedEntry}>
                            Copy selected
                        </Button>
                        <Button variant="contained" startIcon={<Refresh />} onClick={handleRefresh} disabled={listLoading || entriesLoading}>
                            Refresh
                        </Button>
                    </Stack>
                </Stack>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SummaryCard
                            title="Total Log Files"
                            value={summary.totalFiles}
                            helperText="Across all configured log sources."
                            icon={<Description />}
                            tone="primary"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SummaryCard
                            title="PM2 Files"
                            value={summary.pm2Files}
                            helperText="Process output and error logs from PM2."
                            icon={<FolderOpen />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SummaryCard
                            title="Error Log Files"
                            value={summary.errorFiles}
                            helperText="Files flagged as error-oriented logs."
                            icon={<ErrorOutline />}
                            tone="error"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SummaryCard
                            title="Latest Updated"
                            value={summary.latestLog ? formatDateTime(summary.latestLog.modifiedAt) : 'No logs'}
                            helperText={summary.latestLog ? summary.latestLog.filename : 'No readable logs found.'}
                            icon={<WarningAmber />}
                            tone="warning"
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4} lg={4}>
                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                            <Stack spacing={2.5}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        Log Files
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Choose a file to inspect. PM2 is prioritised automatically when available.
                                    </Typography>
                                </Box>

                                <TextField
                                    label="Search files"
                                    value={fileSearchTerm}
                                    onChange={(event) => setFileSearchTerm(event.target.value)}
                                    placeholder="Filename, source or type"
                                    size="small"
                                    fullWidth
                                />

                                {listLoading && availableLogs.length === 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Stack spacing={2.5}>
                                        {groupedLogs.map((source) => (
                                            <Box key={source.id}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                        {source.label}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        color={source.available ? 'primary' : 'warning'}
                                                        label={source.available ? `${source.logs.length} files` : 'Unavailable'}
                                                    />
                                                </Stack>

                                                {!source.available && (
                                                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                                                        {source.message || 'This log source is not available on the current server.'}
                                                    </Alert>
                                                )}

                                                {source.available && source.logs.length === 0 && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                        No log files match the current search.
                                                    </Typography>
                                                )}

                                                {source.available && source.logs.length > 0 && (
                                                    <List
                                                        disablePadding
                                                        sx={{
                                                            borderRadius: 2,
                                                            bgcolor: 'grey.50',
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                        }}
                                                    >
                                                        {source.logs.map((log) => {
                                                            const logKey = getLogKey(log);
                                                            return (
                                                                <ListItemButton
                                                                    key={logKey}
                                                                    selected={selectedLogKey === logKey}
                                                                    onClick={() => setSelectedLogKey(logKey)}
                                                                    sx={{
                                                                        alignItems: 'flex-start',
                                                                        borderBottom: '1px solid',
                                                                        borderColor: 'divider',
                                                                        '&:last-of-type': { borderBottom: 'none' },
                                                                    }}
                                                                >
                                                                    <ListItemText
                                                                        primary={(
                                                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                                                                                <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                                                                                    {log.filename}
                                                                                </Typography>
                                                                                <Chip size="small" label={log.type} variant="outlined" />
                                                                            </Stack>
                                                                        )}
                                                                        secondary={(
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {formatBytes(log.sizeBytes)} · {formatDateTime(log.modifiedAt)}
                                                                            </Typography>
                                                                        )}
                                                                    />
                                                                </ListItemButton>
                                                            );
                                                        })}
                                                    </List>
                                                )}
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={8} lg={8}>
                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
                            <Stack spacing={2.5}>
                                <Box>
                                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {selectedLog ? selectedLog.filename : 'Select a log file'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {selectedLog
                                                    ? `${selectedLog.sourceLabel} · ${formatBytes(selectedLog.sizeBytes)} · updated ${formatDateTime(selectedLog.modifiedAt)}`
                                                    : 'Choose a log file from the left to inspect its latest entries.'}
                                            </Typography>
                                        </Box>

                                        {selectedLog && (
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                <Chip label={selectedLog.sourceLabel} color="primary" variant="outlined" />
                                                <Chip label={selectedLog.type} variant="outlined" />
                                                {logMeta?.truncated && (
                                                    <Chip
                                                        label={`Showing bounded read (${formatBytes(logMeta.bytesRead)} of ${formatBytes(logMeta.totalBytes)})`}
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Stack>
                                        )}
                                    </Stack>
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Search log entries"
                                            value={lineSearchInput}
                                            onChange={(event) => setLineSearchInput(event.target.value)}
                                            placeholder="Message, timestamp or tag"
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4} md={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="severity-filter-label">Severity</InputLabel>
                                            <Select
                                                labelId="severity-filter-label"
                                                label="Severity"
                                                value={severityFilter}
                                                onChange={(event) => setSeverityFilter(event.target.value)}
                                            >
                                                <MenuItem value="all">All levels</MenuItem>
                                                <MenuItem value="error">Error</MenuItem>
                                                <MenuItem value="warning">Warning</MenuItem>
                                                <MenuItem value="info">Info</MenuItem>
                                                <MenuItem value="debug">Debug</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4} md={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="direction-filter-label">Order</InputLabel>
                                            <Select
                                                labelId="direction-filter-label"
                                                label="Order"
                                                value={direction}
                                                onChange={(event) => setDirection(event.target.value)}
                                            >
                                                <MenuItem value="desc">Newest first</MenuItem>
                                                <MenuItem value="asc">Oldest first</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4} md={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="line-limit-label">Entries</InputLabel>
                                            <Select
                                                labelId="line-limit-label"
                                                label="Entries"
                                                value={lineLimit}
                                                onChange={(event) => setLineLimit(Number(event.target.value))}
                                            >
                                                {LINE_LIMIT_OPTIONS.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ height: '100%' }}>
                                            <Tooltip title="Automatically refresh the file list and selected file">
                                                <Switch
                                                    checked={autoRefreshEnabled}
                                                    onChange={(event) => setAutoRefreshEnabled(event.target.checked)}
                                                    inputProps={{ 'aria-label': 'Toggle auto refresh' }}
                                                />
                                            </Tooltip>
                                            <FormControl size="small" sx={{ minWidth: 110 }} disabled={!autoRefreshEnabled}>
                                                <InputLabel id="auto-refresh-label">Refresh</InputLabel>
                                                <Select
                                                    labelId="auto-refresh-label"
                                                    label="Refresh"
                                                    value={autoRefreshSeconds}
                                                    onChange={(event) => setAutoRefreshSeconds(Number(event.target.value))}
                                                >
                                                    {AUTO_REFRESH_OPTIONS.map((seconds) => (
                                                        <MenuItem key={seconds} value={seconds}>
                                                            {seconds}s
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </Grid>
                                </Grid>

                                <Divider />

                                {entriesLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : !selectedLog ? (
                                    <Alert severity="info">Select a log file to start viewing entries.</Alert>
                                ) : logEntries.length === 0 ? (
                                    <Alert severity="info">
                                        No log entries matched the current filters in the bounded section of this file.
                                    </Alert>
                                ) : (
                                    <>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                            <Typography variant="body2" color="text.secondary">
                                                Showing {logEntries.length} entries{typeof logMeta?.total === 'number' ? ` (matched ${logMeta.total})` : ''}.
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {logMeta?.modifiedAt ? `File updated ${formatDateTime(logMeta.modifiedAt)}` : ''}
                                            </Typography>
                                        </Stack>

                                        <TableContainer
                                            ref={viewerRef}
                                            sx={{
                                                maxHeight: 520,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Table stickyHeader aria-label="application logs table" size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ minWidth: 160 }}>Time</TableCell>
                                                        <TableCell sx={{ minWidth: 110 }}>Level</TableCell>
                                                        <TableCell sx={{ minWidth: 110 }}>Tag</TableCell>
                                                        <TableCell>Message</TableCell>
                                                        <TableCell align="right" sx={{ minWidth: 90 }}>Lines</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {logEntries.map((entry) => (
                                                        <TableRow
                                                            key={entry.id}
                                                            hover
                                                            selected={selectedEntryId === entry.id}
                                                            onClick={() => setSelectedEntryId(entry.id)}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {entry.timestamp || 'No timestamp'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    label={entry.level || 'info'}
                                                                    color={LEVEL_CHIP_COLOR[entry.level] || 'default'}
                                                                    variant={entry.level === 'info' ? 'outlined' : 'filled'}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {entry.tag ? (
                                                                    <Chip size="small" variant="outlined" label={entry.tag} />
                                                                ) : (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        -
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box
                                                                    sx={{
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                        fontFamily: 'Consolas, Monaco, monospace',
                                                                        fontSize: '0.8rem',
                                                                        lineHeight: 1.55,
                                                                        color: 'text.primary',
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 3,
                                                                        WebkitBoxOrient: 'vertical',
                                                                        overflow: 'hidden',
                                                                        maxWidth: 560,
                                                                    }}
                                                                >
                                                                    {getEntryDisplayMessage(entry) || '(No detail text)'}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body2">{entry.lineCount || 1}</Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        {selectedEntry && (
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    bgcolor: 'grey.50',
                                                }}
                                            >
                                                <Stack spacing={1.5}>
                                                    <Stack
                                                        direction={{ xs: 'column', md: 'row' }}
                                                        justifyContent="space-between"
                                                        alignItems={{ xs: 'flex-start', md: 'center' }}
                                                        spacing={1}
                                                    >
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            Selected Log Details
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                            <Chip size="small" label={selectedEntry.timestamp || 'No timestamp'} variant="outlined" />
                                                            <Chip
                                                                size="small"
                                                                label={selectedEntry.level || 'info'}
                                                                color={LEVEL_CHIP_COLOR[selectedEntry.level] || 'default'}
                                                                variant={selectedEntry.level === 'info' ? 'outlined' : 'filled'}
                                                            />
                                                            {selectedEntry.tag && (
                                                                <Chip size="small" label={selectedEntry.tag} variant="outlined" />
                                                            )}
                                                            <Chip size="small" label={`${selectedEntry.lineCount || 1} lines`} variant="outlined" />
                                                        </Stack>
                                                    </Stack>

                                                    <Box
                                                        component="pre"
                                                        sx={{
                                                            m: 0,
                                                            p: 2,
                                                            borderRadius: 1.5,
                                                            bgcolor: '#111827',
                                                            color: '#f9fafb',
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                            fontFamily: 'Consolas, Monaco, monospace',
                                                            fontSize: '0.82rem',
                                                            lineHeight: 1.6,
                                                            maxHeight: 280,
                                                            overflow: 'auto',
                                                        }}
                                                    >
                                                        {getEntryDisplayMessage(selectedEntry) || selectedEntry.message || '(No detail text)'}
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        )}
                                    </>
                                )}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Layout>
    );
};

export default Logs;
